import { makeStyles, Theme } from '@material-ui/core/styles';

/**
 * The panel's own drop shadow, cast leftwards over the list it slides across.
 *
 * A literal rather than a palette entry for the reason `OfferLetterTemplates`'
 * `fdsTokens` states about its own two shadows: `fds-tokens` is colours only, so
 * a shadow has no generated source to take. MUI's Drawer elevation stacks three
 * neutral-black layers that spread in every direction, which reads as a floating
 * card rather than as a panel anchored to the right edge.
 */
const panelShadow = (theme: Theme) =>
  `-8px 0 40px color-mix(in srgb, ${theme.palette.common.gray800} 16%, transparent)`;

/**
 * Spacing is on the 2px grid, 16px default and 8px for the dense level rows —
 * the two are never mixed inside one region. Colours come from the FDS theme
 * palette; no raw hex.
 */
export const useStyles = makeStyles(theme => ({
  page: {
    padding: 24,
  },
  // Sits directly under the page title, so it takes the tighter 8px rather than
  // the page's default 16px: title and subtitle are one unit, and spacing them
  // like separate blocks reads as two unrelated sentences.
  subtitle: {
    marginTop: 8,
  },
  pageTabs: {
    marginTop: 24,
  },
  // Runs the full width under the strip, as the design has it — the line is the
  // boundary of the whole tabbed region, not an underline for the tabs.
  pageTabsDivider: {
    marginBottom: 16,
  },
  // One row, as the design has it: Create on the left, the filter and the
  // search together on the right. Wraps rather than shrinks — the three
  // controls and their gaps need about 600px, and under that the right-hand
  // pair drops to its own line instead of squeezing the search field past
  // legibility.
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 16,
    marginBottom: 16,
  },
  // Right-aligned by taking the row's free space rather than by justifying the
  // row itself, so the two stay together — and stay right — once the row wraps.
  toolbarFilters: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    marginLeft: 'auto',
  },
  // MUI's own `iconSizeMedium` rule sizes an `endIcon` at 20px, which lands the
  // caret at about the label's cap height and reads as a second glyph rather
  // than an affordance; the design has it distinctly smaller than the text.
  // Targeted through the button rather than by a class on the icon, because
  // `.MuiButton-iconSizeMedium > *:first-child` outranks a single class and the
  // override silently does nothing.
  createButton: {
    '& .MuiButton-endIcon > *:first-child': {
      fontSize: 14,
    },
  },
  // `ListItemIcon` reserves 56px by default — a gutter for a settings list, not
  // for a two-item menu, where it strands the labels halfway across the popover.
  // 8px to the label is the dense gap, which is what a compact menu row is.
  //
  // gray500 here, unlike `dragHandle` below, which deliberately takes gray600
  // because gray500 on white is 2.68:1 and a control has to clear 3:1. These
  // icons are not controls and carry no information: each sits beside its own
  // visible label, is `aria-hidden`, and the whole row is the click target. So
  // 1.4.11 does not reach them, and gray500 is the design's own value.
  menuItemIcon: {
    minWidth: 'auto',
    marginRight: 8,
    color: theme.palette.common.gray500,
    '& svg': {
      fontSize: 16,
    },
  },
  // The Offers | Openings filter. `RadioGroup row` is a flex row already; this
  // only has to stop the segments spacing themselves apart, because they share
  // their border and a gap would leave the join drawn twice with a hole in it.
  segmentGroup: {
    flexWrap: 'nowrap',
  },
  // One segment. A `FormControlLabel`, so the resets come first: MUI offsets it
  // by -11px to hang a radio glyph in the page gutter and adds 16px on the
  // right for the next field, neither of which applies to a label that is the
  // control's whole visible surface.
  segment: {
    alignItems: 'center',
    border: `1px solid ${theme.palette.common.gray300}`,
    color: theme.palette.common.gray600,
    display: 'flex',
    height: 32,
    justifyContent: 'center',
    margin: 0,
    padding: '0 16px',
    // Collapses the shared edge onto one line, the way a segmented control
    // reads. Radius only on the outer corners, so the pair is one shape.
    '&:first-child': {
      borderRadius: '4px 0 0 4px',
    },
    '&:last-child': {
      borderRadius: '0 4px 4px 0',
      marginLeft: -1,
    },
    '&:hover': {
      background: theme.palette.common.gray100,
    },
    // The radio is hidden, so it has no border or background of its own to
    // change on focus and the ring has to be stated here, on the label the
    // keyboard user can actually see. `:focus-within` rather than a class from
    // MUI, which puts `Mui-focusVisible` on the element being hidden.
    '&:focus-within': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
      // Above the neighbour so the ring is not clipped by the shared edge.
      zIndex: 2,
    },
    // The label text, which `FormControlLabel` renders as a `body1` Typography —
    // 16px in this theme, so the size is stated rather than inherited: 14px is
    // what the button and the search field beside it read at. Medium weight is
    // what marks a segment, selected or not, as a control rather than prose.
    //
    // Stated in px against `content-hierarchy`'s rule about hardcoding the type
    // scale, because no variant carries this pair: `body2` is 14px but bakes
    // `color: gray600` into the variant, which would fight the selected blue,
    // and it is not medium weight. A variant that fits would replace both lines.
    '& .MuiFormControlLabel-label': {
      color: 'inherit',
      fontSize: 14,
      fontWeight: 500,
    },
  },
  // The compact size, for a group inside a level card rather than in the page
  // toolbar. Shorter and tighter, so it reads as a setting on the field above it
  // instead of competing with the card's own heading — which is what the design
  // draws. The label stays at the 14px `segment` sets: the design's 12px is
  // below the smallest size anything on this surface asks a person to read.
  segmentDense: {
    height: 28,
    padding: '0 12px',
  },
  // Driven by the container's comparison rather than by `input:checked`: `:has`
  // is the only CSS route to styling a label from its own descendant's state,
  // and this build still ships to browsers without it.
  segmentSelected: {
    // blue50 under blue400 is 4.67:1, so the chosen label still clears AA for
    // normal text. blue100, the next step up the ramp, is 4.36:1 and does not.
    background: theme.palette.common.blue50,
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
    // The blue outline is what carries the state at 5.1:1 — the blue50 fill is
    // 1.09:1 against white and would be colour doing the job alone. Raised
    // above its neighbour so the 1px the two overlap by paints blue rather than
    // leaving the other segment's grey line showing through.
    zIndex: 1,
    '&:hover': {
      background: theme.palette.common.blue100,
    },
  },
  // Visually hidden, not `display: none`, which would take the input out of the
  // accessibility tree with the glyph and leave a radio group no keyboard can
  // reach. The input stays focusable, checkable and arrow-navigable.
  segmentRadio: {
    border: 0,
    clip: 'rect(0 0 0 0)',
    height: 1,
    margin: -1,
    overflow: 'hidden',
    padding: 0,
    position: 'absolute',
    whiteSpace: 'nowrap',
    width: 1,
  },
  // Wraps the table, so the border and the radius are the container's rather
  // than being drawn on the first and last rows. `overflow-x` here and not on
  // the table: the Trigger column holds account-authored position and field
  // names, so this is the one column whose width nothing here controls, and a
  // narrow window has to scroll the table rather than the page.
  list: {
    border: `1px solid ${theme.palette.common.gray200}`,
    borderRadius: 4,
    overflowX: 'auto',
    background: theme.palette.common.white,
  },
  // `auto`, not MUI's `fixed`: the Rule and Trigger columns hold text of a
  // length this page does not choose, and dividing the width equally gives Type
  // and Enabled — both of which hold one short word — as much room as the
  // summary sentence.
  table: {
    tableLayout: 'auto',
  },
  // The banded header row. `gray100` is what the design system's own
  // `TableCell` fills a `head` cell with (`fountain-ui-components/src/Table/
  // TableCell`), so the band is the system's value rather than a match for the
  // design's `#eceef3`. `gray200` is the nearer hex and is not available: it
  // puts `text.secondary` at 4.20:1, under AA, where `gray100` holds 4.51:1.
  // It is also the divider colour, so a `gray200` band would swallow the rule
  // under it.
  //
  // The medium face, not `fontWeight: 500` — this theme carries weight in the
  // family, so a numeric weight synthesizes a faux-bold out of the Book face.
  // `fontWeight: normal` stays to keep MUI's own `TableCell head` 500 from
  // stacking that synthesis on top of the Medium face. Unbanded, a medium
  // header outweighed the rule names under it; inside the band it reads as the
  // label of a region instead of as content.
  //
  // Size stays at `body2`'s 14px. The design draws these at 13px and the scale
  // has no step there — `h5`/`h6` are 12px and `h4`/`body2` are 14px — so the
  // choice is a hardcoded size or the nearest role, and it is the role.
  //
  // Note the type role a header "should" take, `subtitle1`, is unusable in this
  // package: the FDS theme sets `subtitle1` to **24px on a 42px line and renders
  // it as an `<h6>`**, not the 14px helper style the scale describes. Measured,
  // not assumed. Every cell in this table is `body2` for the same reason, and
  // reaching for `subtitle1` to make something secondary will produce a heading
  // the size of the page title.
  headerCell: {
    padding: '0 16px',
    height: 40,
    background: theme.palette.common.gray100,
    borderBottom: `1px solid ${theme.palette.common.gray200}`,
    color: theme.palette.text.secondary,
    fontFamily: theme.typography.h4.fontFamily,
    fontWeight: 'normal',
    whiteSpace: 'nowrap',
  },
  // Settings density (~52px), not the compact 40px of a recruiter queue: this
  // list is read occasionally, and the first cell carries a second line on a
  // multi-level rule. `verticalAlign: top` because of that second line — centred,
  // a two-line first cell floats the chips in every other column out of line
  // with the name they belong to.
  // `body2`'s own 16px leading is too tight for the cells that wrap — a Trigger
  // sentence at 16px leading on 14px text has its lines almost touching. 20px is
  // what the delete confirmation settled on for the same reason.
  cell: {
    padding: '8px 16px',
    height: 52,
    verticalAlign: 'top',
    borderBottom: `1px solid ${theme.palette.common.gray200}`,
    '& .MuiTypography-root': {
      lineHeight: '20px',
    },
  },
  // A floor under the Trigger column, so a narrow window scrolls the table
  // instead of shredding the summary.
  //
  // `table-layout: auto` shrinks a column to its min-content, which for a
  // sentence is its longest *word* — so this is the column that gives way first
  // and hardest. Measured at an 820px page: without the floor the cell fell to
  // ~55px and wrapped "Position: Regional Manager, District Manager +1 and
  // Sign-on bonus greater than 10000" over seven lines, one or two words each.
  // That reads as a layout that works and is really unreadable, which is the
  // same failure `conditionControls` guards against in the editor.
  //
  // 220px holds "Compensation: outside pay range" on one line. The container's
  // own `overflow-x` is what absorbs the rest.
  triggerCell: {
    minWidth: 220,
  },
  // The loading and empty states, which span every column.
  stateCell: {
    padding: 16,
    textAlign: 'center',
    borderBottom: 'none',
  },
  rule: {
    '&:hover': {
      background: theme.palette.common.gray100,
    },
  },
  rowClickable: {
    cursor: 'pointer',
  },
  // A disabled rule stays listed — it is configuration the admin still owns, and
  // hiding it would make "why is this not firing?" unanswerable from this page.
  // Dimming pairs with the "Off" badge: opacity alone would be colour-as-state.
  ruleDisabled: {
    opacity: 0.5,
  },
  // The name and the badges beside it. Wraps, because an account names its own
  // rules and a long one plus two badges does not fit a column this page shares
  // with four others.
  ruleName: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  // The level count, and the note on a legacy-managed rule. Both are the second
  // line of the first cell, so both take the tight 4px — they belong to the name
  // above them, not to the row.
  ruleMeta: {
    marginTop: 4,
  },
  // The row's clickable name. A real `<button>` rather than a `role="button"` on
  // the `<tr>`: the row also holds a switch and two icon buttons, and nesting
  // those inside a widget is an axe `nested-interactive` violation — it is also
  // what made Enter on an icon button fire the row's handler as well, because
  // the row caught the bubbled event. A native button suppresses Space's page
  // scroll for free.
  nameButton: {
    border: 0,
    margin: 0,
    padding: 0,
    background: 'transparent',
    font: 'inherit',
    textAlign: 'left',
    color: 'inherit',
    cursor: 'pointer',
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
      borderRadius: 2,
    },
  },
  // Screen-reader-only. Same declarations as the two other places in this app
  // that need it (`OfferLetterTemplates/CountRatio`,
  // `Openings/components/OpeningsTable/styles`).
  visuallyHidden: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
  typeChip: {
    // The word carries the meaning; the tint is reinforcement. A colourblind
    // reader gets "Offer" either way, which is why this is a labelled chip and
    // not a coloured dot.
    fontWeight: 500,
  },
  // The chip's leading entity glyph.
  //
  // `size="small"` pins MUI's icon slot to an 18px box, which next to a 12px
  // label is the loudest thing in the cell. `1em` hands the size back to the
  // chip's own font-size — MUI sets that to 13px on the `Chip` root, the value
  // the design draws the glyph at — so nothing here restates a pixel.
  //
  // `inherit` for the colour: MUI defaults the slot to its own `grey[700]`
  // (`#616161` — Material's stock ramp, which this theme never overrides, so it
  // is not an FDS grey at all), which would put a grey glyph inside a green or
  // blue chip. Inheriting takes `green800` or `blue600` from the chip, both of
  // which clear AA on their tint.
  typeChipIcon: {
    width: '1em',
    height: '1em',
    color: 'inherit',
  },
  // green800 on green100 is 10.4:1. NOT green600 (#009e52), which is 3.1:1 on
  // the same tint — a fail for 12px chip text, and the trap this palette sets
  // twice: the mid-tone token looks like the "green" one and is only legible on
  // white.
  typeOffer: {
    background: theme.palette.common.green100,
    color: theme.palette.common.green800,
  },
  // blue600 on blue50 is 6.5:1, the pairing `OfferLetterTemplates` settled on
  // for the same reason. Deliberately not blue500 on blue50 (4.53:1, clears AA
  // by 0.03) nor blue500 on blue100 (4.23:1, fails).
  typeOpening: {
    background: theme.palette.common.blue50,
    color: theme.palette.common.blue600,
  },
  // The Enabled cell's contents: the rule's switch, and on a single-level rule
  // the inlined level's "Off" badge. A wrapper rather than making the `TableCell`
  // itself a flex container, which would take it out of the table's own width
  // computation.
  stateGroup: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  // Approver names wrap rather than truncate: a name half-shown is not an
  // approver anyone can identify, and this cell is the answer to "who signs
  // this off".
  approvers: {
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    flexWrap: 'wrap',
  },
  // gray700 on gray100 is 9.2:1. gray600, which is what the neutral chip
  // elsewhere in this app uses on white, computes to 4.51:1 against this tint —
  // over AA by 0.01, which is not a margin to ship 12px text on.
  approverChip: {
    background: theme.palette.common.gray100,
    color: theme.palette.common.gray700,
  },
  // A level of a multi-level rule, indented and joined to its rule by a branch
  // line. Drawn on the first cell rather than as a border on the row, because a
  // `<tr>` cannot carry a border in the default `border-collapse: collapse`.
  //
  // The vertical stops halfway down the last level's cell rather than running to
  // the bottom of it: the line's job is to connect this row to the one above,
  // and carried to the floor it points at the next *rule*.
  levelCell: {
    paddingLeft: 32,
    position: 'relative',
    '&::before': {
      content: '""',
      position: 'absolute',
      left: 20,
      top: 0,
      bottom: 0,
      borderLeft: `1px solid ${theme.palette.common.gray300}`,
    },
    '&::after': {
      content: '""',
      position: 'absolute',
      left: 20,
      top: 24,
      width: 8,
      borderTop: `1px solid ${theme.palette.common.gray300}`,
    },
  },
  // Stops the branch line at the elbow on the rule's last level, so it does not
  // run on into the rule below and read as connecting the two.
  //
  // A class rather than `:last-child`, because a level row is only the table's
  // last child when its rule happens to be the last in the list — every rule in
  // the middle of the page would keep the full-height line. The component knows
  // which level is last; CSS cannot express "last of a run of sibling rows".
  levelCellLast: {
    '&::before': {
      bottom: 'auto',
      height: 25,
    },
  },
  // No `cursor: pointer` here — it goes on `rowClickable`, which a level row only
  // takes when it has somewhere to go. A legacy-managed rule can have several
  // levels and opens no editor, so an unconditional pointer would promise a
  // click that does nothing.
  //
  // The resting tint says the row is subordinate to the white rule row above it,
  // reinforcing the branch line rather than replacing it. It has to sit between
  // white and `gray100`, and the ramp has no step there — `gray100` itself is the
  // hover, and taking it at rest would leave the hover nowhere to go and make a
  // level row read as heavy as the header band. Half-strength `gray100` composites
  // to `#f8f9fb` on the table's white paper, one unit off the design's `#f9fafb`,
  // and keeps `text.secondary` at 4.75:1. The alpha is over the token, so it is
  // still the palette's colour and not a hex of its own.
  levelRow: {
    background: `color-mix(in srgb, ${theme.palette.common.gray100} 50%, transparent)`,
    '&:hover': {
      background: theme.palette.common.gray100,
    },
  },
  // The two icon buttons. Right-aligned and sized to its contents so the Trigger
  // column keeps the width the row does not need here.
  actionsCell: {
    width: 88,
    textAlign: 'right',
    whiteSpace: 'nowrap',
  },
  rowAction: {
    color: theme.palette.common.gray600,
  },
  rowActionIcon: {
    fontSize: 18,
  },
  empty: {
    padding: 48,
    textAlign: 'center',
    borderBottom: 'none',
  },
  emptyBody: {
    marginTop: 8,
  },
  emptyAction: {
    marginTop: 16,
  },
  error: {
    display: 'flex',
    alignItems: 'center',
    gap: 16,
    padding: 16,
    marginBottom: 16,
    border: `1px solid ${theme.palette.common.red400}`,
    background: theme.palette.common.red100,
    borderRadius: 4,
  },
  // MUI's own Drawer paper is `overflow-y: auto`, so without this the paper is
  // the scroller and the panel's header and footer scroll away with everything
  // else — the pinning below would be inert. Handing the scroll to one region
  // inside the paper is the whole change.
  panelPaper: {
    overflow: 'hidden',
    boxShadow: panelShadow(theme),
  },
  // 640px per the design. Capped at the viewport so the panel does not overflow
  // a narrow window — a drawer wider than the screen puts its own actions off
  // the right edge.
  //
  // A full-height column of three regions: a pinned header, the one scrolling
  // body, and a pinned footer. The padding and the 16px rhythm belong to those
  // regions rather than to this element, or the header would scroll its own
  // inset past the body's first field.
  panel: {
    width: 640,
    maxWidth: '100vw',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
  },
  // Title and close, on one line and never scrolled. `flexShrink: 0` because a
  // flex item's default is to shrink: with a long rule in the body the header
  // would otherwise give up its own padding before the body gave up any height.
  //
  // Tighter under the title than over it, as the design has it: the rule below
  // is the top of the body, so an even inset reads as the title floating in the
  // band rather than sitting on the content it names.
  panelHeader: {
    alignItems: 'center',
    borderBottom: theme.customBorders.gray200,
    display: 'flex',
    flexShrink: 0,
    gap: 16,
    justifyContent: 'space-between',
    padding: '20px 24px 16px',
  },
  // Pulled back by its own padding so the glyph, not the hit area, aligns to the
  // 24px inset — the same trick, and the same two figures, `dialogClose` states.
  //
  // The focus ring is stated outright, as `dragHandle` states its own. MUI's
  // `ButtonBase` sets `outline: 0` unconditionally and ships an empty
  // `focusVisible` class, and this theme overrides neither — so an `IconButton`
  // has no focus indicator at all until one is written. That is the panel's
  // dismiss control, and a keyboard user tabbing to it otherwise gets nothing.
  panelClose: {
    color: theme.palette.common.gray600,
    marginRight: -6,
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
  },
  // The only scroll region. It keeps the panel's 16px rhythm, which the three
  // regions no longer share: the header and footer are single rows.
  panelBody: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: 16,
    overflowY: 'auto',
    padding: '20px 24px',
  },
  field: {
    marginTop: 8,
  },
  // The intro under the title. `body2`'s 16px line box is the panel's dense
  // default and is set for one-line labels; this is the only paragraph on the
  // surface and wraps to two or three lines, where a line box the same height as
  // the glyphs closes the leading up entirely.
  panelIntro: {
    lineHeight: 1.55,
    // 4px on top of the body's 16px gap, landing on the 20 the design leaves
    // under this paragraph. It is the one gap the design sets apart from the
    // panel's rhythm.
    marginBottom: 4,
  },
  // The rules between the name, the scope question and the levels. Three
  // questions, asked in order, and the panel's 16px rhythm alone spaces them
  // like a run of fields rather than like the sections they are.
  // Compounded onto `.MuiDivider-root` rather than left as a bare class. The
  // theme sets `MuiDivider.root` to gray300, which ties this on specificity, and
  // at a tie the winner is whichever sheet attached last. `conditionSelect`
  // states the same reason for the same trick.
  //
  // Nothing visible rides on it today — `theme.ts` collapses gray200 and gray300
  // onto one Ripple neutral, so both sides of that tie paint the same colour.
  // The rule is here to hold the design's token, not to fix a live defect.
  panelDivider: {
    '&.MuiDivider-root': {
      backgroundColor: theme.palette.common.gray200,
    },
  },
  // A label and its control, as one block in the body's flex column. Without it
  // the two are separate flex items and the body's 16px gap opens between a
  // label and the box it names.
  fieldGroup: {
    display: 'flex',
    flexDirection: 'column',
  },
  // `CONDITIONS` / `APPROVERS` / `BACKUP APPROVERS` — the caption naming a
  // section of the level card, at the 12px uppercase gray700 the design settled
  // on (AXHE-5024). 12 because this theme's scale has no 11px step, and gray700
  // because the design's gray500 is 2.68:1 as text.
  //
  // `h5` is the 12px role. The uppercase and the tracking are stated here rather
  // than taken from a role because no role in this theme carries either — which
  // is why this needed a design decision rather than a lookup.
  //
  // Doubled onto itself so it beats `conditionSelect`'s own three-class rule on
  // the picker's label node, rather than turning on which key is declared later.
  sectionCaption: {
    ...theme.typography.h5,
    color: theme.palette.common.gray700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
  // The same caption, for a picker that draws its own label. It restyles that
  // one node and nothing else.
  //
  // The properties cannot go on the wrapper the way `sectionCaption` puts them
  // on a heading: `text-transform` and `letter-spacing` inherit, so a wrapper
  // carrying them upper-cases every chip, option and typed value in the control.
  //
  // Doubled onto itself so it beats `conditionSelect`'s own three-class rule on
  // the same node, rather than turning on which key is declared later.
  captionedField: {
    '&& .MuiGrid-root > .MuiTypography-body2': {
      ...theme.typography.h5,
      color: theme.palette.common.gray700,
      letterSpacing: '0.06em',
      textTransform: 'uppercase',
    },
  },
  // A label sitting above its control, matching the one the design system's own
  // picker draws — the same 14px/16px gray600 with a 4px gutter that "Approvers"
  // and "Backup approvers" already render at. One label style across the panel
  // is the point: a field the panel labels itself must not read as a different
  // kind of field from one the picker labels.
  fieldLabel: {
    ...theme.typography.body2,
    color: theme.palette.common.gray600,
    display: 'block',
    marginBottom: 4,
  },
  // The asterisk on a required label. `aria-hidden` in the markup — `required`
  // on the input is what reaches a screen reader — so this is the visual half
  // only, and it is the one place the error colour is spent on a resting state
  // because the design asks for it there.
  // red600, not the red400 the design draws. This is text, and red400 measures
  // 4.498:1 on white — under AA's 4.5 for normal text by a rounding error, which
  // is still under it. red600 is 6.12:1 and is the token `OfferLetterTemplates`
  // already reserves for red text, with red400 kept for borders.
  requiredMark: {
    color: theme.palette.common.red600,
  },
  // The panel's one text-input treatment: a bordered 34px box with the label
  // above it, not MUI's underline with a floating label (AXHE-4989 records the
  // same gap on the document editor). Applied to the rule name, the level name
  // and both escalation controls, so the four cannot drift apart.
  //
  // Built on the `standard` variant with the underline switched off rather than
  // on `outlined`: MUI's outlined variant draws the label into a notch in its
  // own fieldset, which is the treatment this replaces.
  outlinedField: {
    '& .MuiInputBase-root': {
      ...theme.typography.body2,
      color: theme.palette.common.gray800,
      background: theme.palette.common.white,
      // gray400, which `theme.ts` resolves to Ripple's `neutral-300` — Hire's
      // control border, and a visible step darker than the `neutral-200` the
      // containers on this panel draw.
      //
      // It measures 1.84:1, under the 3:1 SC 1.4.11 asks of the boundary that
      // identifies a control. That is deliberate and not this surface's to fix:
      // Hire's field borders sit at 1.2 to 1.8:1 product-wide, so a darker hex
      // here would make one panel disagree with every other field rather than
      // fix anything. The fix is a Ripple `border-input` token, on AXHE-5059.
      border: `1px solid ${theme.palette.common.gray400}`,
      borderRadius: 4,
      boxSizing: 'border-box',
      height: 34,
      padding: '0 10px',
    },
    '& .MuiInputBase-input': {
      padding: 0,
    },
    // Focus and error are drawn on the border and a soft ring, the way the
    // design draws them. `focus-within` rather than `:focus`, because the
    // focused node is the inner input and the border is on the root.
    '& .MuiInputBase-root.Mui-focused': {
      borderColor: theme.palette.primary.main,
      boxShadow: `0 0 0 3px ${theme.palette.common.blue50}`,
    },
    '& .MuiInputBase-root.Mui-error': {
      borderColor: theme.palette.common.red400,
      boxShadow: `0 0 0 3px ${theme.palette.common.red100}`,
    },
    // Two things, and the second is not redundant with MUI's own rule.
    //
    // MUI tints the select's display element on focus. Inside a bordered box
    // that reads as a filled field rather than a focused one, and the border and
    // ring above already carry the state.
    //
    // The right inset is restated because the display element carries
    // `.MuiInputBase-input` as well, and the rule above zeroes that padding at
    // the same specificity MUI reserves the caret's space at. At a tie the winner
    // is sheet order, and losing it runs a long value — "Business days" — under
    // the caret.
    '& .MuiSelect-select': {
      background: 'none',
      paddingRight: 24,
    },
  },
  // The note under the "All …" choice, inset past the radio column so it reads
  // as belonging to the choice above it rather than to the group. Its own
  // leading, for the reason `panelIntro` gives: it wraps to two lines and
  // `body2`'s line box is set for one.
  scopeAllNote: {
    lineHeight: 1.55,
    paddingLeft: 24,
  },
  // The two scope choices on one line, as the design has them: they are one
  // question with two answers, and stacked they read as two separate settings.
  // Wraps rather than shrinks, for the reason `conditionControls` gives — the
  // panel is capped at the viewport and the longer label is a full sentence.
  scopeRadios: {
    flexWrap: 'wrap',
    gap: 24,
  },
  // "Apply this rule to" and everything it governs (AXHE-4467). Its own dense
  // region at 8px throughout — the legend, the radio pair, the notes and the
  // condition rows are one question, and the panel's 16px would space them like
  // four separate blocks.
  scopeSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  // Taken from the theme's h4 rather than restated: 14px medium is the section
  // label one step under the panel's 18px h3 title. MUI's own FormLabel default
  // would render it at body size in the secondary text colour, which reads as
  // helper copy rather than as the heading for the choice below it.
  scopeLegend: {
    ...theme.typography.h4,
    color: theme.palette.common.gray800,
  },
  // A non-ideal state on a level card the admin has to be told about — approvers
  // the save will drop, a level the rule skips, a backup roster with nobody on
  // it. Coloured *and* worded: the sentence carries the meaning on its own, so
  // the colour is reinforcement rather than the signal.
  //
  // `orange800`, not `theme.palette.warning.main`. This theme leaves MUI's
  // warning palette at MUI's own orange (#ff9800), which measures **2.16:1 on
  // white** — under AA's 4.5:1 for normal text, so the line was unreadable for
  // exactly the reader who most needs a warning. `dialogCallout` below already
  // states the same divergence and reaches for the FDS ramp for the same reason.
  //
  // orange800 is the only step on that ramp that clears AA as text on white
  // (12.73:1; orange600 is 3.11 and orange400 is 2.74). It is dark, so it
  // reinforces less than a mid-tone would — which is the right way round: "never
  // use colour alone" already puts the meaning in the sentence, so a
  // reinforcement that fails AA is strictly worse than a subtle one that passes.
  // A tinted block would carry it better and is what `dialogCallout` does; that
  // is a design change for the whole surface, not a fix to one line.
  inlineWarning: {
    marginTop: 4,
    color: theme.palette.common.orange800,
  },
  // Holds the level cards, and nothing else — it exists so the drag context has
  // an element to own. Deliberately no `gap`: the cards space themselves, for
  // the reason given on `levelCard`.
  levelList: {
    display: 'flex',
    flexDirection: 'column',
  },
  // The region a lifted level will land in, tinted for as long as one is over
  // it. It reinforces the gap the drag opens at the insertion point rather than
  // being the only sign of where the level is going.
  levelListDropTarget: {
    background: theme.palette.common.gray100,
    borderRadius: 4,
  },
  levelCard: {
    // gray300 and radius 8, as the design draws it. Only the radius changes what
    // renders: `theme.ts` collapses gray200 and gray300 onto Ripple's
    // `neutral-200`, so the two are one colour here. The token records which one
    // the design asked for, against the day the ramp separates them again.
    //
    // No 1.4.11 duty either way. A card is a container, not a control, so its
    // edge is not a boundary anyone has to perceive to operate something — which
    // is why this stays at the design's token while `outlinedField` above does
    // not.
    border: `1px solid ${theme.palette.common.gray300}`,
    borderRadius: 8,
    // No padding of its own: the card is a header band and two bordered
    // sections, and each one insets itself. A padded card would push those
    // borders off its own edges and they would stop reading as divisions of it.
    //
    // `relative` so the arrow below can be hung in the gap under the card. It is
    // out of flow deliberately — the drag library measures every card's margin
    // box, and a connector in that flow would be measured as part of one card
    // and displace the rest by its height on every lift.
    position: 'relative',
    // Opaque so a lifted card reads as sitting *above* the tinted drop region
    // rather than as part of it.
    background: theme.palette.common.white,
    // Spacing between cards is a margin rather than the list's `gap`: the drag
    // library measures each card's margin box to work out how far the others
    // move while one is lifted, and a flex gap belongs to no box — so the cards
    // below would shift 16px short of where the lifted one is going to land.
    //
    // On the *top* edge, reset by `:first-child` rather than on the bottom edge
    // reset by `:last-child`. While a level is lifted the library appends its
    // placeholder as the list's final child, so no card is `:last-child` and a
    // bottom-margin reset switches itself off mid-drag: the list grows by 16px
    // and everything under it — Add level, the note, Cancel and Save — steps
    // down and snaps back on the drop. The first child is the first card
    // whether or not a drag is in progress.
    //
    // 28px, the gap the design leaves: it is what the arrow connector hangs in,
    // and the arrow needs clear space at both ends or it reads as a join between
    // two touching boxes rather than as a step from one to the next.
    marginTop: 28,
    '&:first-child': {
      marginTop: 0,
    },
  },
  // The downward arrow between adjacent cards — the levels are a sequence, and
  // the arrow is what says so at a glance. Decorative: it repeats what "Level 1"
  // and "Level 2" already say, so it is `aria-hidden` in the markup.
  //
  // Drawn in CSS rather than as an SVG, the way `levelCell` draws the table's
  // branch line: a 1px shaft and a square rotated onto its corner. gray300 is
  // the same token that line uses.
  //
  // Hung in the 28px gap under the card it belongs to, so it moves with that
  // card during a drag instead of sitting between two cards that are moving.
  // 3px clear of the card above and 9px clear of the one below, which is where
  // the design's own 16px glyph sits in that gap.
  levelArrow: {
    bottom: -19,
    height: 16,
    left: '50%',
    marginLeft: -7,
    position: 'absolute',
    width: 14,
    '&::before': {
      borderLeft: `1px solid ${theme.palette.common.gray300}`,
      bottom: 5,
      content: '""',
      left: 7,
      position: 'absolute',
      top: 0,
    },
    '&::after': {
      borderBottom: `1px solid ${theme.palette.common.gray300}`,
      borderRight: `1px solid ${theme.palette.common.gray300}`,
      bottom: 3,
      content: '""',
      height: 7,
      left: 3,
      position: 'absolute',
      transform: 'rotate(45deg)',
      width: 7,
    },
  },
  // The lifted card, drawn the way the workflow editor draws a dragged row: a
  // darker border and a soft glow, so the state is carried by more than colour.
  levelCardDragging: {
    borderColor: theme.palette.common.gray400,
    boxShadow: `0 0 2px 2px ${theme.palette.common.gray200}`,
  },
  // The card's header band: drag handle, "Level N", the level's optional name,
  // and remove — one tinted strip, so the card reads as a titled block rather
  // than as a run of fields. Tinted *and* ruled, because the tint alone
  // disappears against the drop-target highlight the list takes during a drag.
  //
  // No `justifyContent`: the name input takes the row's free space, which is
  // what puts remove at the far end. A `space-between` would fight it.
  // Wraps, for the reason `conditionControls` gives one row down: the panel is
  // capped at the viewport, and without it the name field shrinks to its floor
  // and clips the name instead of the row moving it to a second line.
  levelCardHeader: {
    alignItems: 'center',
    background: theme.palette.common.gray100,
    borderBottom: theme.customBorders.gray200,
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8,
    padding: '10px 14px',
  },
  // "Level N". h4 (14px medium) is this panel's section-label role — the same
  // one `scopeLegend` takes — at the secondary colour, so the band names the
  // card without competing with the panel's own h3 title. The mock's 13px has
  // no step on this theme's scale; 14px medium is the nearest role, and picking
  // a role over a literal size is what keeps it in step with the labels around
  // it. It never wraps: the number is the card's identity.
  levelNumber: {
    color: theme.palette.common.gray600,
    whiteSpace: 'nowrap',
  },
  // The optional level name, inline in the band. It takes the free space so the
  // band fills out at any panel width, and stops shrinking at 180px — matching
  // the basis, for the reason `conditionField` gives: an input's min-content
  // width is almost nothing, so without the floor flex shrinks it past the
  // length of a real level name and clips it with no ellipsis. At the floor the
  // header wraps instead, and the name gets a line of its own.
  //
  // The input is sized down from MUI's 16px body default to the 14px the band's
  // other text runs at, which is also what brings the control to the ~28px the
  // band is drawn around.
  //
  // The placeholder is coloured explicitly. MUI's default is the input colour at
  // `opacity: 0.42`, which is gray800 at 42% — **2.54:1 on this band**, well
  // under AA, and this field's placeholder is the only visible cue it has (its
  // label is an `aria-label`, since the band has no room for a visible one). At
  // full opacity gray600 measures **4.51:1** on gray100, which clears AA for
  // normal text while staying lighter than the gray800 a typed name renders in —
  // so it still reads as a prompt rather than as a value.
  // Declared AFTER `outlinedField`, and that is load-bearing: both classes land
  // on this field through `clsx`, both target `.MuiInputBase-root`, and both
  // carry the same specificity — so the later key in this object is the one that
  // wins. Moving it above `outlinedField` silently restores the 34px height.
  levelNameField: {
    flex: '1 1 180px',
    minWidth: 180,
    // 28px rather than the 34 `outlinedField` gives a field standing on its own:
    // this one sits inside the band, whose height the design draws around it.
    '& .MuiInputBase-root': {
      height: 28,
    },
    '& .MuiInputBase-input': {
      '&::placeholder': {
        color: theme.palette.common.gray600,
        opacity: 1,
      },
    },
  },
  // One division of a level card — its conditions, then its approvers. The rule
  // under the conditions is what separates "does this level apply" from "who
  // signs off", which is the order the card is read in.
  //
  // 8px of the top inset is the condition builder's and the approver field's own
  // top margin, so the padding is stated at 4 to land on the 12 the card is
  // drawn to. Stated here rather than reset on the children: both sections carry
  // that margin, and resetting it would only move the same figure elsewhere.
  levelSection: {
    padding: '4px 14px 12px',
  },
  levelSectionDivided: {
    borderBottom: theme.customBorders.gray200,
  },
  // 28px square. The design draws every control in this band at 27, and
  // `layout-composition` puts sizing on the 2px grid — a rank-1 rule, so the odd
  // pixel rounds up rather than being transcribed. The band stays 49px either
  // way: its height comes from the 28px name field beside these.
  //
  // The glyph is 14px and the box around it is what makes the target reachable.
  // Stated here rather than left to the icon's own size, which is what left the
  // handle a 20px hit area beside a full-size remove.
  dragHandle: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    width: 28,
    height: 28,
    borderRadius: 4,
    // gray600, not the gray500 the workflow editor's handle uses: a control has
    // to clear 3:1 and gray500 on white is 2.68:1.
    color: theme.palette.common.gray600,
    cursor: 'grab',
    // The handle is keyboard-operable, and an icon has no border or background
    // of its own to change — so it needs a focus ring stated outright.
    '&:focus-visible': {
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
    '&:hover': {
      background: theme.palette.common.gray200,
    },
  },
  // Remove, at the far end of the band. The same box the handle takes, so the
  // two ends of the row are the same weight — MUI's `IconButton` draws a
  // 22px circle in its own translucent black, which is neither.
  levelRemove: {
    width: 28,
    height: 28,
    borderRadius: 4,
    color: theme.palette.common.gray600,
    '&:hover': {
      background: theme.palette.common.gray200,
    },
  },
  // The backup-approver block, set off from the primary roster above it by a rule
  // rather than by a heading (AXHE-3932). It is a second roster for the same
  // level, so it groups with the primaries — the border is what stops the two
  // reading as one list, and 12px above and below is the tighter of the panel's
  // two spacings because this is one region and not a new section.
  backupSection: {
    borderTop: theme.customBorders.gray200,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    marginTop: 12,
    paddingTop: 12,
  },
  // The roster and its remove control on one line. Wraps for the reason
  // `conditionControls` gives: the panel is capped at the viewport, and a select
  // whose min-content width is almost nothing shrinks past legibility rather than
  // moving the button down.
  backupRosterRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    gap: 8,
  },
  backupRoster: {
    flex: '1 1 240px',
    minWidth: 240,
  },
  // Aligned to the roster's input line, not to the top of its box: the picker
  // stacks a 14px/16px label and the 4px `conditionSelect` pins above its
  // control, so a button at the top of the row sits level with the label instead
  // of with the control it acts on. The 28px box is what takes the target from
  // the 14px icon to a real pointer target, and it is the size every other
  // control on this panel takes — see `dragHandle` for why it is 28 rather than
  // the 27 the design draws.
  backupRemove: {
    marginTop: 20,
    width: 28,
    height: 28,
    borderRadius: 4,
    color: theme.palette.common.gray600,
    '&:hover': {
      background: theme.palette.common.gray200,
    },
  },
  // "If no response after [ 2 ] [ Days ]" — one sentence read across static text
  // and two controls, so the pieces are centred on one line at the panel's dense
  // 8px. Wraps rather than shrinks, for the reason `conditionControls` gives.
  escalateTimer: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
  },
  // Merged onto `escalateTimer`, so it reaches every text run the sentence splits
  // into rather than one leading fragment — a translation decides where those
  // runs fall, so none of them is "the first words". Secondary grey, as the
  // design has it: the sentence frames two controls rather than being read on
  // its own, and at body colour it competed with the values inside them.
  // gray600 clears AA on white for normal text, so it stays legible as the
  // frame it is.
  //
  // No `whiteSpace: nowrap`. It was written when this class sat on a span holding
  // only the fixed English prefix; on the row it makes every run unbreakable, and
  // a flex item's automatic minimum size is its min-content width — so a long
  // translated run would overflow the panel instead of wrapping. `escalateTimer`'s
  // `flexWrap` still moves whole runs onto the next line, which is the wrapping
  // that was actually wanted.
  escalateTimerText: {
    color: theme.palette.common.gray600,
  },
  // 72px, matching the mock: the field holds one or two digits, and a wider box
  // reads as room for a figure the range does not allow.
  //
  // Nothing resets MUI's label gutter, because there is no gutter to reset. That
  // rule is `label + &` (`Input.js`'s `formControl` style) — an adjacent-sibling
  // selector, not a class — and a `TextField` renders an `InputLabel` only when
  // given a `label`. This field has none, so no `<label>` is emitted and the
  // margin is already 0. `escalateUnitField` below is label-less too and needs no
  // override either, which is the corroboration: if the gutter applied, the
  // picker beside this field would sit 16px out of line.
  escalateAfterField: {
    width: 72,
  },
  // Sized to hold "Business days", the longest option a stored level can carry.
  escalateUnitField: {
    width: 160,
  },
  // "Approvals needed" and its Any/All segments, on the line under the approver
  // picker (AXHE-3899). A row rather than a stacked label so the setting reads as
  // a qualifier on the field above it; it wraps because the panel is capped at
  // the viewport and the label plus two segments need about 260px.
  approverLogic: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 8,
    marginTop: 8,
  },
  // Centred under the cards, as the design has it. `alignSelf` rather than a
  // margin: the panel body is a flex column, so a button left to its default
  // `stretch` spans the full width and reads as a region rather than as the one
  // control that appends to the stack above it.
  addLevel: {
    alignSelf: 'center',
  },
  // "Add level" and "Add backup approvers" — the two affordances that append to
  // the stack they sit under. Drawn as links rather than as buttons: a filled or
  // bordered control here would carry the same weight as the footer's Save, and
  // these add a row to a form the admin has not committed yet.
  //
  // `h4` (14px medium) rather than MUI's 16px button type, which is the size the
  // panel's own title runs at one step up — a link inside a card must not be the
  // largest text on it. The `+` is the design system's own `PlusIcon`, at the
  // 12px its viewBox is drawn for.
  addLink: {
    ...theme.typography.h4,
    color: theme.palette.primary.main,
    padding: '4px 8px',
    textTransform: 'none',
    '& .MuiButton-startIcon': {
      marginRight: 4,
    },
    // Compounded for the reason `panelDivider` gives: MUI sizes a start icon
    // through `.MuiButton-iconSizeMedium > *:first-child`, which is two class
    // tokens and ties a bare descendant selector here.
    '& .MuiButton-startIcon .MuiSvgIcon-root': {
      fontSize: 12,
    },
    '&:hover': {
      background: theme.palette.common.blue50,
    },
  },
  // The footer, pinned for the reason the header is: on a rule with four levels
  // the save was below the fold, so the last thing an admin needs was the one
  // thing scrolling found. `flexShrink: 0` for the reason `panelHeader` gives.
  panelActions: {
    borderTop: theme.customBorders.gray200,
    display: 'flex',
    flexShrink: 0,
    gap: 8,
    justifyContent: 'flex-end',
    padding: '16px 24px',
  },
  // Both footer buttons. 36px at 14px type, as the design draws them: MUI's own
  // 40px at 16px is the page-level button, and the panel's copy runs at 14
  // throughout — a footer a size larger than everything above it reads as a
  // second surface.
  //
  // Cancel is outlined rather than a bare text button. It is one of two actions
  // on the same row, and with no border it reads as a link beside a button
  // rather than as the other half of the choice.
  panelButton: {
    ...theme.typography.h4,
    height: 36,
    padding: '0 16px',
    textTransform: 'none',
    // MUI's outlined border is `rgba(0, 0, 0, 0.23)`, a translucent black that
    // sits beside the panel's own gray300 rules and reads a shade warmer than
    // every other border on the surface.
    '&.MuiButton-outlined': {
      borderColor: theme.palette.common.gray300,
    },
  },
  // 448px, not `Dialog`'s `sm` default: the body is one sentence plus a warning,
  // and a line longer than this reads as a paragraph the admin skims.
  dialogPaper: {
    width: 448,
    maxWidth: '100%',
  },
  // The header, footer and content insets are 16/32 and 32 — the same figures
  // the FDS modal shell used, so this dialog still lines up with every other
  // modal in Hire now that it no longer inherits them.
  dialogTitle: {
    alignItems: 'center',
    display: 'flex',
    justifyContent: 'space-between',
    padding: '16px 32px',
  },
  // Pulled back by its own padding so the glyph, not the hit area, aligns to
  // the 32px inset. The target stays 32px square.
  dialogClose: {
    color: theme.palette.common.gray600,
    marginRight: -6,
  },
  // The confirmation's body is a stack of blocks — sentence, warning callout,
  // permanence note — rather than one paragraph, so they are spaced by the
  // column and none of them carries a margin of its own.
  // `dividers` draws the two rules, but in MUI's own rgba(0,0,0,0.12) — restated
  // as gray200, which is what every other modal in Hire separates on.
  dialogContent: {
    borderBottom: theme.customBorders.gray200,
    borderTop: theme.customBorders.gray200,
    display: 'flex',
    flexDirection: 'column',
    gap: 16,
    padding: 32,
  },
  // gray800 rather than `body2`'s own gray600: this is the sentence the whole
  // dialog is about, and the note under the callout is the secondary one.
  dialogSentence: {
    color: theme.palette.common.gray800,
    lineHeight: '20px',
  },
  // MUI's `Alert` computes its warning palette from `theme.palette.warning`,
  // which this theme leaves at MUI's own orange rather than the FDS ramp — so
  // the tint and text are stated here in FDS tokens instead. orange800 on
  // orange100 is 11.6:1.
  //
  // The rest is fitting a component whose defaults assume one line to a warning
  // that runs to three: `Alert` sets its icon at 22px with `padding: 7px 0`,
  // which on a three-line block leaves the glyph floating well below the first
  // line it belongs to. 16px matches the body's cap height, and the 2px nudge is
  // the usual first-line mid-line correction.
  dialogCallout: {
    backgroundColor: theme.palette.common.orange100,
    border: `1px solid ${theme.palette.common.orange200}`,
    color: theme.palette.common.orange800,
    padding: '8px 12px',
    '& .MuiAlert-icon': {
      alignItems: 'flex-start',
      fontSize: 16,
      marginRight: 8,
      marginTop: 2,
      opacity: 1,
      padding: 0,
    },
    '& .MuiAlert-message': {
      lineHeight: '20px',
      padding: 0,
    },
  },
  // Left at `body2`'s gray600 — 5.0:1 on white, so it is subordinate to the
  // sentence above without dropping under AA the way gray500 (2.68:1) would.
  dialogFootnote: {
    lineHeight: '20px',
  },
  dialogError: {
    color: theme.palette.common.red600,
    lineHeight: '20px',
  },
  // The footer band. `DialogContent dividers` draws the rule above it, so this
  // only supplies the tint and the inset.
  dialogActions: {
    backgroundColor: theme.palette.common.gray100,
    padding: '16px 32px',
  },
  // Both footer buttons. MUI's outlined default is a 1px rgba(0,0,0,0.23)
  // border on transparent, which is neither the FDS ramp nor legible against
  // the tinted band — so the fill, border, height and type size are stated in
  // tokens.
  //
  // Not `size="small"`: that is 13px type on 9px of horizontal padding, which
  // is smaller than anything else on this surface. The size wanted here is the
  // one Hire calls a small button — `customHeights.smallButton` tall with
  // `caption`'s 14px — which is what these measured at before the shell swap,
  // and MUI's default padding is what gets them there.
  dialogButton: {
    backgroundColor: theme.palette.common.white,
    border: `1px solid ${theme.palette.common.gray300}`,
    color: theme.palette.common.gray800,
    fontSize: theme.typography.caption.fontSize,
    height: theme.customHeights.smallButton,
    '&:hover': {
      backgroundColor: theme.palette.common.gray100,
      borderColor: theme.palette.common.gray400,
    },
  },
  // The destructive one. Same box as its neighbour, in the error role.
  //
  // The `border` and `color` below *are* the red outline, not a patch over a
  // design-system bug. No FDS `Button` renders on this surface, so a fix to
  // FDS's own `secondary negative` variant does not reach here — deleting these
  // leaves MUI's default outlined border, which is grey.
  //
  // Scoped `:not(.Mui-disabled)` rather than left bare: MUI's own
  // `.MuiButton-outlined.Mui-disabled` border rule is two classes deep, so a
  // single-class rule here would tie it and win on sheet order alone — and this
  // button is disabled on every open, while the pending count loads. Excluding
  // it lets the disabled state fall through to MUI's, which is what every other
  // disabled button on the surface looks like.
  //
  // The border and the label take different reds, which is the pairing the
  // design specifies. red400 (`common.error`) computes to 4.4987:1 on white —
  // under AA's 4.5 for text, over the 3:1 a border has to clear. red600 is
  // 6.12:1, so the label clears AA while the box keeps the error role's own
  // token. `dangerButton` below reaches for red600 too, though it has no border
  // to pair it with — an icon has only the 3:1 bar to clear, so that one is a
  // free choice rather than this one's requirement.
  dialogDeleteButton: {
    backgroundColor: theme.palette.common.white,
    fontSize: theme.typography.caption.fontSize,
    height: theme.customHeights.smallButton,
    '&:not(.Mui-disabled)': {
      border: `1px solid ${theme.palette.common.error}`,
      color: theme.palette.common.red600,
    },
    '&:not(.Mui-disabled):hover': {
      backgroundColor: theme.palette.common.red100,
      border: `1px solid ${theme.palette.common.error}`,
    },
  },
  // The affirmative counterpart of `dialogDeleteButton`, in the primary role: a
  // bulk approval is the dialog's progressive action, so it takes the filled
  // primary the queue's own Approve controls carry rather than the outlined
  // error box its two siblings demote their confirms to. Only the box is stated
  // — same type size and height as `dialogButton`, so the footer reads as one
  // row — and the fill comes from `contained primary`, which is the theme's.
  dialogConfirmButton: {
    fontSize: theme.typography.caption.fontSize,
    height: theme.customHeights.smallButton,
  },
  // The row's delete action. Destructive actions never carry primary weight — an
  // icon button in the error role, last in the row so Edit stays the affordance
  // the eye lands on. red600 (6.12:1 on white) rather than the red400 FDS gives
  // a `negative` button, which computes to 4.4987:1 — a hair under AA's 4.5 for
  // text, though 3:1 is the bar an icon has to clear anyway.
  //
  // Every destructive label across Approvals now takes red600, whichever
  // component paints it: this one, the two dialog confirms above, and the FDS
  // buttons the queue renders (`ApprovalsQueue/styles.ts`, `destructiveButton`).
  // The design system's own red400 stays where it belongs — on the borders, and
  // on every other app's buttons, which repainting is its own change.
  dangerButton: {
    color: theme.palette.common.red600,
  },
  conditions: {
    marginTop: 8,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  // One condition per row, read left to right as a sentence — "Offer field /
  // Sign-on bonus / greater than / 10000" (AXHE-4473, AXHE-4383). The column here
  // holds that line, plus whichever modifier the row's type carries underneath it
  // — the compare-with checkbox on an offer field, the opening-pay-range checkbox
  // on a pay condition, nothing on the rest — and any note qualifying it. The
  // controls on the line itself never stack.
  conditionRow: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
  // Wraps rather than shrinks: the panel is capped at the viewport, so on a
  // narrow window there is not room for three controls side by side. Without
  // this the row still fits — a select is `overflow: hidden`, so its min-content
  // width is almost nothing and flex shrinks all three past legibility instead,
  // which reads as a layout that works and is really "does not equ…".
  conditionControls: {
    display: 'flex',
    flexWrap: 'wrap',
    // Not `center`: a control carrying a validation message is taller than its
    // neighbours, and centring would then float the others out of line with it.
    alignItems: 'flex-start',
    gap: 8,
  },
  // One picker in a condition row. The flex sizing stays on the classes below,
  // which the row hands to each control; this carries what every picker needs.
  conditionSelect: {
    // The design system's own label, reached through the container it sits in so
    // that both declarations win on specificity rather than on which stylesheet
    // attached first.
    //
    // Descendant rather than a child of this element: a multi wraps its control
    // in one more `Grid` than a single does, so a chain anchored on `&` matched
    // every single-select label and no multi's. That is why "Location groups"
    // and "Backup approvers" rendered title-cased beside a sentence-cased
    // "Condition type" on the same row. Still three classes, so the specificity
    // the two declarations below rely on is unchanged.
    '& .MuiGrid-root > .MuiTypography-body2': {
      // The design system's label style capitalizes every word, so a
      // sentence-case label — "Compare with field" — renders as "Compare With
      // Field". Sentence case is the rule for a field label, and the label is
      // the design system's to render, so the case is corrected here rather
      // than a second label written.
      textTransform: 'none',
      // The same 4px the design system asks for, stated here because its rule
      // and MUI's `Typography` reset (`margin: 0`) carry equal specificity: at
      // equal specificity the winner is whichever stylesheet attached last,
      // which depends on render order. `conditionRemove` is positioned against
      // the height this gives, so it cannot be left to that.
      marginBottom: 4,
    },
  },
  // A disabled picker has to look disabled. The design system's control paints
  // no disabled state at all — `StyledControl` keeps `cursor: pointer` and its
  // default border whatever `isDisabled` says — so the operator picker on a
  // fresh row is indistinguishable from one that works. Two cues, only one of
  // them colour: the field is tinted and its border goes dashed.
  //
  // Scoped to this surface because the design system's own control is shared,
  // and because the two pickers in the workflow editor never pass `isDisabled`
  // — this is the first surface where the gap is reachable.
  conditionSelectDisabled: {
    '& .MuiGrid-container': {
      background: theme.palette.common.gray100,
      borderStyle: 'dashed',
      cursor: 'default',
    },
  },
  // A picker's validation message, in flow under the control it belongs to. The
  // design system positions its own error caption absolutely, 24px below the
  // control, which on this row lands on the compare-with checkbox underneath.
  //
  // Only ever one message per row — `validateCondition` returns a single error —
  // so this never appears beside the typed value input's MUI helper text.
  conditionSelectError: {
    marginTop: 4,
  },
  // The truncation note, in flow under the picker it belongs to for the reason
  // the error above is. Subordinate to the row's own content — this says how
  // complete the list behind the control is, not what the condition holds.
  conditionSelectNote: {
    marginTop: 4,
    color: theme.palette.text.secondary,
  },
  // One entity in a set-membership menu. Padding matches the design system's own
  // option row, so a grouped menu and a flat one sit at the same rhythm.
  //
  // `.focused` is declared after `.selected` so it wins on a row that is both.
  // react-select marks the row under the pointer *and* the row the arrow keys
  // are on with the same flag, so if selection won there would be no way to see
  // where the keyboard is inside a fully chosen group. Selection is still shown:
  // the checkbox carries it, which is why the tint can be spent on focus.
  conditionOptionRow: {
    '&.selected': {
      backgroundColor: theme.palette.common.blue100,
    },
    // A ring, not only a tint. The tints this menu has to work with — a resting
    // row, a chosen one, a header — sit within about 1.3:1 of each other, so a
    // step between two of them is the one mark on the row nobody can see. A ring
    // is a shape rather than a shade and composes with all three, and inset it
    // draws inside the row's own box and shifts nothing.
    '&.focused': {
      backgroundColor: theme.palette.common.gray100,
      boxShadow: `inset 0 0 0 2px ${theme.palette.primary.main}`,
    },
    // Clear of the checkbox on the row above it, which is what says this option
    // is one of the group's rather than one of the list's.
    '&.indented': {
      paddingLeft: 32,
    },
    borderRadius: 3,
    cursor: 'pointer',
    padding: '12px 16px',
  },
  // Inert, so it takes none of the room a real control's hit area would: the row
  // is the control. The tightest step on the scale to the label, which is what
  // the icon rule asks of any glyph sitting against its own text.
  conditionOptionCheckbox: {
    marginRight: 4,
    padding: 0,
    pointerEvents: 'none',
  },
  // One line, ellipsised. An account names its own locations and this control is
  // the narrowest place they are read, so a long name has to end somewhere — and
  // wrapping it would give one option two rows in a menu scanned by row.
  conditionOptionLabel: {
    '&.group': {
      fontWeight: theme.typography.fontWeightBold,
    },
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  // A group header. Tinted rather than ruled, because the rows under it are
  // indented against this one and a rule would read as a divider between two
  // flat lists instead.
  //
  conditionGroupRow: {
    // The same ring `conditionOptionRow` carries, and for the same reason: on a
    // header the rest tint is already the grey an option takes under focus, so
    // the tint alone says nothing at all here.
    '&.focused': {
      backgroundColor: theme.palette.common.gray300,
      boxShadow: `inset 0 0 0 2px ${theme.palette.primary.main}`,
    },
    backgroundColor: theme.palette.common.gray100,
    borderRadius: 3,
    cursor: 'pointer',
    padding: '12px 16px',
  },
  // Right-aligned by taking the row's free space, so it stays at the edge
  // whatever the group is called.
  conditionGroupCount: {
    color: theme.palette.common.gray700,
    marginLeft: 'auto',
    paddingLeft: 16,
  },
  // The bases below are sized against the one number that decides whether a row
  // is a row: a condition sits 518px wide inside a level card, once the 32px
  // remove button and the gap before it are taken off the card's 558. A row of
  // four controls spends 24px more on its own gaps, so the four it draws have to
  // come to 494 or less or the last one wraps to a line of its own.
  //
  // A basis is not the rendered width. A control spends 54px on chrome — 36px of
  // padding, 2px of border, an 8px arrow and the 8px the value keeps clear of it
  // — so a 116px picker shows about 62px of text and truncates past that. That
  // is the trade the one-line row costs, and the design makes it too: its own
  // operator select truncates "greater than".
  //
  // `flex-grow` then hands the slack back in the design's proportions, so a row
  // of three controls is not four widths with a hole at the end.
  //
  // `minWidth` matching the basis is what makes the row wrap rather than shrink
  // on a window narrower than the panel: a select's min-content width is almost
  // nothing, so flex would otherwise crush all four past legibility.
  //
  // So the one-line row is a promise at the panel's own width and not below it.
  // The panel is capped at the viewport, and under about 400px these bases stop
  // fitting and the row wraps by design. Nothing overflows there — wrapping is
  // what keeps Reflow satisfied — but the arithmetic above describes the
  // default width only.

  // Takes the slack, because it is the only control here whose content nobody
  // can bound: an account names its own data keys and attributes. Its basis is
  // therefore the smallest of the four — every name is truncated at some width,
  // so buying it room costs a fixed vocabulary its full text.
  conditionField: {
    flex: '1.4 1 100px',
    minWidth: 100,
  },
  // First in every row, because the type decides what the rest of the row even
  // is, and the only picker sized to show its longest option whole:
  // "Applicant pay" is 88px of text, so 142 is that plus the chrome. Six fixed
  // options, so this width holds for every account.
  conditionType: {
    flex: '0 1 142px',
    minWidth: 142,
  },
  // The attribute row's scope picker. Its own three options are the shortest in
  // any picker here — "Opening", "Location", "Position" — so it is sized by its
  // label instead: "Attribute source" is 103px.
  //
  // Not `conditionType`, which it used to share. That row is the one this layout
  // cannot fit on a line: an offer attribute condition renders five controls,
  // and five pickers need more chrome than 518px holds. The value still wraps,
  // and this is what keeps the four above it from being squeezed for a width the
  // scope picker never needed.
  conditionAttributeScope: {
    flex: '0 1 110px',
    minWidth: 110,
  },
  // Ellipsised, deliberately. "greater than" is 77px and this leaves 62, and the
  // room it would take comes off the field beside it. The design truncates this
  // same control for the same reason, and an operator is re-read from the open
  // menu where a field name is not.
  conditionOperator: {
    flex: '1 1 116px',
    minWidth: 116,
  },
  // The compare-with picker, which holds a field but is sized by its label:
  // "Compare with field" is 121px, and under that the label wrapped to two lines
  // and took its row 16px taller than every other.
  conditionCompareKey: {
    flex: '1 1 124px',
    minWidth: 124,
  },
  // An entity picker holds several selections at once, so it is the widest
  // control in the row and takes the largest share of the slack. The basis is
  // what a location row needs: it carries two of these beside the type picker
  //, and 2 × 160 beside the 142px type picker is 478 inside the 518.
  conditionEntity: {
    flex: '2 1 160px',
    minWidth: 160,
  },
  // The right-hand side, which is a MUI underline input for a date, a number or a
  // free string and a picker for a field that enumerates its values. The picker
  // is what sets this width: an input spends none of the 54px of chrome a select
  // does, so sizing to the input alone left an enumerated value about 40px to
  // render in.
  conditionValue: {
    flex: '1 1 112px',
    minWidth: 112,
  },
  // The typed value input, which stays a MUI text field — a date, a number or a
  // string is entered, not chosen. Its label is absolutely positioned inside the
  // control with the input carrying a 16px top margin, so without this its input
  // line sits 4px above the design system controls beside it.
  conditionTypedValue: {
    marginTop: 4,
  },
  // Matched to the control line: the picker stacks a 14px/16px label and the 4px
  // `conditionSelect` pins above a 32px control, so 20px is where that control
  // starts, and a box of the control's own dimensions lands on it whatever size
  // the icon inside it is — an `IconButton` already centres its child both ways.
  // It is also what takes the target from the 22px the icon alone gives to a
  // full 32px.
  conditionRemove: {
    marginTop: 20,
    width: 32,
    height: 32,
  },
  // Shared by the remove control on a condition row and on a level card. One
  // icon per action across the product, so the two are the same glyph at the
  // same size — the level card's button sits in a header row rather than beside
  // an input, so only the box sizing above is condition-specific.
  removeIcon: {
    fontSize: 16,
    color: theme.palette.common.gray500,
  },
  // Between adjacent conditions, never after the last — it describes a join, and
  // there is nothing after the final row to join it to.
  //
  // `Chip size="small" variant="outlined" clickable` gives the shape and the
  // click behavior. What it does not give is color: MUI's outlined default is
  // a `rgba(0,0,0,.23)` border under `rgba(0,0,0,.87)` text on no fill, three
  // values on no token ramp at all.
  //
  // The three tokens are read at the values this app resolves them to, not at
  // the ones the ramp ships. `theme.ts` maps the FDS grays onto Ripple's neutral
  // ramp, so `gray600` is `#687281` here rather than `#666f89` — and several
  // older comments in this file still quote the pre-Ripple figures, which is
  // what makes the wrong pairing look pre-approved. Measured against `theme.ts`:
  //
  // - `gray700` on the `gray100` fill is 7.84:1. `gray600` is 4.44:1 and misses
  //   AA for normal text, which `fontWeight: 500` does not exempt it from.
  // - `gray600` as the border is 4.87:1 on the card. The design draws this at
  //   `gray300`, which resolves to `#e2e8f0` and gives 1.23:1 — under the 3:1
  //   1.4.11 asks of the boundary identifying a control, and weaker than the
  //   MUI default it replaced. The token is the design's; the contrast rule
  //   outranks it.
  //
  // The margin is what makes it read as a join rather than as a control
  // belonging to the row above — at the section's 8px gap alone it sits directly
  // under that row's compare-with checkbox and groups with it.
  connector: {
    alignSelf: 'flex-start',
    margin: '8px 0',
    background: theme.palette.common.gray100,
    border: `1px solid ${theme.palette.common.gray600}`,
    color: theme.palette.common.gray700,
    // A pill, not the 16px MUI settles on for a 24px chip. The radius is half
    // the height either way, so this only holds once the height changes.
    borderRadius: 999,
    // 500 rather than `theme.typography.fontWeightMedium`, which is this same
    // number. The token is typed as csstype's `Property.FontWeight`, and that
    // includes `string & {}` where MUI v4's `CreateCSSProperties` still wants
    // the narrower `FontWeightProperty` — so assigning it here fails the
    // `makeStyles` overload, and every class in this sheet then infers as
    // `never`. A nested selector is not checked that strictly, which is why
    // `conditionOptionLabel` can use the token and this cannot.
    fontWeight: 500,
    // Hover turns the outline and the label brand and leaves the fill where it
    // is, which is the design's own hover.
    //
    // Both of MUI's own classes are named so the selector reaches four classes.
    // `.MuiChip-clickable.MuiChip-outlined:hover` paints a translucent overlay
    // over whatever fill is under it, and at three classes it beats a rule
    // written on this class alone — so the fill moved on hover and the brand
    // outline arrived over a background that had shifted with it.
    '&.MuiChip-clickable.MuiChip-outlined:hover': {
      background: theme.palette.common.gray100,
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
    },
    // Separate from hover, and a ring rather than a border change. Sharing one
    // selector showed a keyboard state to a pointer user for as long as the
    // click kept focus, and left the indicator as a 1px edge. `panelClose`
    // states its own ring for the reason given there: `ButtonBase` sets
    // `outline: 0` and ships an empty `focusVisible` class, so an indicator has
    // to be written.
    '&.MuiChip-clickable.MuiChip-outlined:focus-visible': {
      background: theme.palette.common.gray100,
      outline: `2px solid ${theme.palette.primary.main}`,
      outlineOffset: 2,
    },
  },
  // Holds the builder's place while the field catalog loads, so the section does
  // not read as "this level has no conditions" for the length of the request.
  conditionsPlaceholder: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    padding: 8,
  },
}));
